import React, { useState, useEffect } from 'react';
import * as api from '../../api';
import { Plus, Trash2, Edit2, Link as LinkIcon, Image as ImageIcon } from 'lucide-react';

export default function AdsManagerView({ showTemporarySuccess }) {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  
  // Form State
  const [imageUrl, setImageUrl] = useState('');
  const [targetUrl, setTargetUrl] = useState('');
  const [location, setLocation] = useState('home_top');
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    loadAds();
  }, []);

  const loadAds = async () => {
    try {
      const res = await api.getAds();
      if (res.success) {
        setAds(res.data);
      }
    } catch (e) {
      console.warn('Failed to load ads:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { image_url: imageUrl, target_url: targetUrl, location, is_active: isActive };
      const res = await api.createAd(payload);
      if (res.success) {
        showTemporarySuccess('Advertisement created successfully!');
        setShowCreateModal(false);
        resetForm();
        loadAds();
      }
    } catch (e) {
      alert(e.message || 'Failed to create ad');
    }
  };

  const handleToggleActive = async (ad) => {
    try {
      const res = await api.updateAd(ad.id, { ...ad, is_active: !ad.is_active });
      if (res.success) {
        showTemporarySuccess(`Ad ${ad.is_active ? 'deactivated' : 'activated'}`);
        loadAds();
      }
    } catch (e) {
      alert(e.message || 'Failed to update ad');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this advertisement?')) return;
    try {
      const res = await api.deleteAd(id);
      if (res.success) {
        showTemporarySuccess('Advertisement deleted');
        loadAds();
      }
    } catch (e) {
      alert(e.message || 'Failed to delete ad');
    }
  };

  const resetForm = () => {
    setImageUrl('');
    setTargetUrl('');
    setLocation('home_top');
    setIsActive(true);
  };

  return (
    <div className="animate-fade">
      <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h2>Advertisements</h2>
          <p className="subtitle">Manage banner ads across the mobile app</p>
        </div>
        <button className="btn btn-purple" onClick={() => setShowCreateModal(true)}>
          <Plus size={18} style={{ marginRight: '8px' }} /> Create Ad
        </button>
      </div>

      <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px', marginTop: '20px' }}>
        {loading ? (
          <p>Loading advertisements...</p>
        ) : ads.length === 0 ? (
          <p>No advertisements found.</p>
        ) : (
          ads.map(ad => (
            <div key={ad.id} className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <div style={{ position: 'relative', height: '120px', background: '#111116' }}>
                {ad.image_url ? (
                  <img src={ad.image_url} alt="Ad Banner" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <div style={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center' }}>
                    <ImageIcon size={32} color="var(--text-3)" />
                  </div>
                )}
                <div style={{ position: 'absolute', top: 10, right: 10 }}>
                  <span className={`badge ${ad.is_active ? 'badge-success' : 'badge-danger'}`}>
                    {ad.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
              
              <div style={{ padding: '16px' }}>
                <p style={{ fontWeight: 600, color: 'var(--text-1)', marginBottom: '8px' }}>
                  Location: <span style={{ color: 'var(--neon-purple)' }}>{ad.location}</span>
                </p>
                
                <p style={{ fontSize: '0.8rem', color: 'var(--text-2)', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '16px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  <LinkIcon size={12} /> {ad.target_url}
                </p>
                
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button 
                    className="btn btn-outline" 
                    style={{ flex: 1, padding: '6px' }}
                    onClick={() => handleToggleActive(ad)}
                  >
                    {ad.is_active ? 'Deactivate' : 'Activate'}
                  </button>
                  <button 
                    className="btn btn-outline" 
                    style={{ padding: '6px', color: 'var(--danger)' }}
                    onClick={() => handleDelete(ad.id)}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {showCreateModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, 
          background: 'rgba(0,0,0,0.8)', zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <div className="card" style={{ width: '100%', maxWidth: '500px', margin: '20px' }}>
            <h3 style={{ marginBottom: '20px' }}>Create New Advertisement</h3>
            <form onSubmit={handleCreateSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label>Banner Image URL *</label>
                <input 
                  type="url" 
                  className="form-control" 
                  placeholder="https://example.com/image.jpg"
                  value={imageUrl} 
                  onChange={e => setImageUrl(e.target.value)} 
                  required 
                />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label>Target URL (Link to open) *</label>
                <input 
                  type="url" 
                  className="form-control" 
                  placeholder="https://example.com/promo"
                  value={targetUrl} 
                  onChange={e => setTargetUrl(e.target.value)} 
                  required 
                />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label>Location in App *</label>
                <select className="form-control" value={location} onChange={e => setLocation(e.target.value)}>
                  <option value="home_top">Home (Top)</option>
                  <option value="profile_bottom">Profile (Bottom)</option>
                  <option value="room_detail">Room Detail (Bottom)</option>
                </select>
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input 
                    type="checkbox" 
                    checked={isActive} 
                    onChange={e => setIsActive(e.target.checked)} 
                    style={{ width: '16px', height: '16px' }}
                  />
                  <span>Active immediately</span>
                </label>
              </div>
              
              <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
                <button type="submit" className="btn btn-purple" style={{ flex: 1 }}>Save Advertisement</button>
                <button type="button" className="btn btn-outline" style={{ flex: 1 }} onClick={() => setShowCreateModal(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
